import {useMutation, UseMutationOptions} from 'react-query'

import {
  OrderChangeResponse,
  SubmitOrderChangeParams
} from '../../../../features/rmc/orderManagement/types/orderChange.types'
import {LatestOrderChangeRequest} from '../../../../features/rmc/orderManagement/types/orderChangeBanner.types'
import {mutations} from '../../mutations'
import {hubQueryClient} from '../../QueryProvider'

type UseSubmitOrderChangeOptions = Omit<
  UseMutationOptions<OrderChangeResponse, Error, SubmitOrderChangeParams>,
  'mutationFn'
>

/**
 * React Query mutation hook for submitting order changes.
 *
 * Handles the API call to PATCH /ecs/order-management/v2/Change/{orderId}
 * and invalidates relevant queries on success.
 *
 * On success, optimistically seeds the latestOrderChangeRequest cache with a
 * 'sent' status entry so that the OrderDetailsSM loading overlay appears
 * instantly when the user navigates back from the result screen, without
 * waiting for a network round-trip.
 *
 * @example
 * const {mutate, isLoading, error} = useSubmitOrderChange({
 *   onSuccess: (data) => console.log('Success:', data),
 *   onError: (error) => console.error('Error:', error)
 * })
 */
export const useSubmitOrderChange = (options?: UseSubmitOrderChangeOptions) => {
  return useMutation(mutations.submitOrderChange, {
    ...options,
    onSuccess: (data, variables, context) => {
      // Optimistically seed the latestOrderChangeRequest cache so that the
      // order details overlay/spinner appears instantly when the user navigates
      // back from the result screen (no fetch delay).
      const optimisticRequest: Partial<LatestOrderChangeRequest> = {
        requestId: data.requestId,
        status: 'sent',
        changesApplied: false,
        orderId: variables.orderId,
        requestData: {requiresManualApproval: data.requiresManualApproval ?? false}
      }
      hubQueryClient.setQueryData(
        ['latestOrderChangeRequest', variables.orderId],
        optimisticRequest as LatestOrderChangeRequest
      )

      // Invalidate order-related queries to refresh data
      void hubQueryClient.invalidateQueries(['order', variables.orderId])
      void hubQueryClient.invalidateQueries('orders')
      void hubQueryClient.invalidateQueries('orderChangeRequestStatus')
      void hubQueryClient.invalidateQueries('orderChangeOptions')
      void hubQueryClient.invalidateQueries('orderCancellationOptions')

      options?.onSuccess?.(data, variables, context)
    },
    onError: (error, variables, context) => {
      options?.onError?.(error, variables, context)
    }
  })
}
